import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output, ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ManagedCompaniesService } from 'src/app/core/services/managed-companies.service';
import { ThemeService } from 'src/app/core/services/theme.service';
import { UserProfileDataService } from 'src/app/core/services/user-profile.service';
import { AiChatComponent } from 'src/app/pages/ai/ai-chat/ai-chat.component';
import { CohortDataService } from 'src/app/pages/buyer/setup-cohorts/services/cohort.service';
import { isCompanyDetailsV2Path } from 'src/app/core/http/company-id-context';
import { buildTechEditorCompanySwitchUrl } from 'src/app/core/routing/tech-editor-route-context';
import { GlobalService } from '../../../../portal/search-api/services/global.service';
import { AuthService } from '../../../core/services/auth.service';

type SearchActiveTab = 'products' | 'companies' | 'contacts';

const SEARCH_ACTIVE_TAB_STORAGE_KEY = 'searchActiveTab';

@Component({
  selector: 'header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [GlobalService],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnDestroy {
  @ViewChild(MatMenuTrigger, { static: false }) trigger: MatMenuTrigger;
  @ViewChild(MatMenu, { static: false }) menu: MatMenu;
  @ViewChild('companyMenuTrigger', { static: false }) companyMenuTrigger: MatMenuTrigger;
  @ViewChild('userMenuTrigger', { static: false }) userMenuTrigger: MatMenuTrigger;

  @Input() userName: string;
  @Input() searchTerm: string;

  @Input() userNameAs: string;
  @Input() isUserAs: boolean;

  @Output() logout = new EventEmitter();
  @Output() logoutAs = new EventEmitter();

  suggestionResponse: Array<{ result: string }>;
  searchControl = new FormControl();

  isBayerCaddy: boolean;
  currentUrl: string;
  companies = [];

  selectedCompany;
  profileCompanyId;

  isRhetorikThemeActive = false;

  showAiChat = true;

  isPeopleViewer = false;

  private activeHeaderMenuTrigger: MatMenuTrigger | null = null;
  private headerMenuCloseTimer: ReturnType<typeof setTimeout> | null = null;

  readonly dialog = inject(MatDialog);

  constructor(
    private searchService: GlobalService,
    public authService: AuthService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    public userProfileDataService: UserProfileDataService,
    private managedCompaniesService: ManagedCompaniesService,
    private cohortDataService: CohortDataService,
    private themeService: ThemeService,
  ) {
    this.activeRoute.queryParams.subscribe(() => {
      this.currentUrl = this.router.url;
    });
    this.isRhetorikThemeActive = this.themeService.isRhetorikTheme();
  }

  async ngOnInit(): Promise<void> {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;
      });
    this.userName = this.authService.getUserClaims()?.name;
    this.isBayerCaddy = JSON.parse(localStorage.getItem('isBayerCaddy'));
    await this.userProfileDataService.getUserData();
    this.profileCompanyId = this.userProfileDataService.userCompanyData?.id;
      this.isPeopleViewer = this.authService.checkPeopleViewerRole();
    this.managedCompaniesService.managedCompanies$.subscribe((companies) => {
      this.companies = companies;
      const newCompany = {
        id: this.userProfileDataService.userCompanyData?.id,
        name: this.userProfileDataService.userCompanyData?.name,
        domain: this.userProfileDataService.userCompanyData?.website,
        selected: false,
      };

      if (!this.companies.find(c => c.id === newCompany.id)) {
        this.companies.push(newCompany);
      }

      this.selectedCompany = this.companies.find((x) => x.selected);
      if (this.selectedCompany) {
        localStorage.setItem('companyId', this.selectedCompany.id);
      } else {
        this.selectedCompany = this.companies.find(
          (x) => x.id == this.profileCompanyId
        );
      }
    });
    const browserPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (!isCompanyDetailsV2Path(browserPath) && !isCompanyDetailsV2Path(this.router.url)) {
      this.cohortDataService.getDefaultCohort().toPromise();
    }
  }

  switchToBuyerCaddy() {
    this.isBayerCaddy = true;
    localStorage.setItem('isBayerCaddy', 'true');
    this.router.navigate(['/buyer-home']);
  }

  switchToSalesCaddy() {
    this.isBayerCaddy = false;
    localStorage.setItem('isBayerCaddy', 'false');
    this.router.navigate(['/home']);
  }

  async gotoCompanyPage() {
    const id =
      localStorage.getItem('companyId') ||
      this.userProfileDataService.userProfileData?.company?.companyId;
    if (!id) return;
    this.router.navigate(['/company-v2', id]);
  }
  isCurrentPage(page: string): boolean {
    return this.currentUrl.startsWith(page);
  }

  ngOnDestroy(): void {
    this.cancelHeaderMenuClose();
  }

  openHeaderMenuOnHover(trigger: MatMenuTrigger): void {
    this.cancelHeaderMenuClose();

    if (this.activeHeaderMenuTrigger && this.activeHeaderMenuTrigger !== trigger) {
      this.activeHeaderMenuTrigger.closeMenu();
    }

    this.activeHeaderMenuTrigger = trigger;
    if (!trigger.menuOpen) {
      trigger.openMenu();
    }
  }

  onHeaderMenuOpened(trigger: MatMenuTrigger): void {
    this.cancelHeaderMenuClose();

    if (this.activeHeaderMenuTrigger && this.activeHeaderMenuTrigger !== trigger) {
      this.activeHeaderMenuTrigger.closeMenu();
    }

    this.activeHeaderMenuTrigger = trigger;
  }

  scheduleHeaderMenuClose(trigger: MatMenuTrigger): void {
    this.cancelHeaderMenuClose();
    this.headerMenuCloseTimer = setTimeout(() => {
      if (this.activeHeaderMenuTrigger === trigger) {
        trigger.closeMenu();
        this.activeHeaderMenuTrigger = null;
      }
      this.headerMenuCloseTimer = null;
    }, 220);
  }

  onHeaderMenuClosed(trigger: MatMenuTrigger): void {
    if (this.activeHeaderMenuTrigger === trigger) {
      this.activeHeaderMenuTrigger = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest(
      '.company-switch-trigger, #header-button-menuTrigger, .company-menu-panel, .user-menu-panel'
    )) {
      return;
    }

    this.cancelHeaderMenuClose();
    this.activeHeaderMenuTrigger?.closeMenu();
    this.activeHeaderMenuTrigger = null;
  }

  private cancelHeaderMenuClose(): void {
    if (this.headerMenuCloseTimer) {
      clearTimeout(this.headerMenuCloseTimer);
      this.headerMenuCloseTimer = null;
    }
  }

  isInsightsMenuActive(): boolean {
    return this.isCurrentPage('/buyer-insights') || this.isCurrentPage('/skills-intelligence');
  }

  getSearchRoute(): string {
    const activeTab = this.readSearchActiveTab();

    if (activeTab === 'companies') {
      return '/products/companies';
    }

    if (activeTab === 'contacts' && this.isPeopleViewer) {
      return '/products/profiles';
    }

    return '/products';
  }

  private readSearchActiveTab(): SearchActiveTab | null {
    let activeTab: string | null = null;

    try {
      activeTab = localStorage.getItem(SEARCH_ACTIVE_TAB_STORAGE_KEY);
    } catch {
      return null;
    }

    return activeTab === 'products' || activeTab === 'companies' || activeTab === 'contacts'
      ? activeTab
      : null;
  }

  changeCompany(company) {
    if (company.id != this.profileCompanyId) {
      this.managedCompaniesService.updateManagedCompanies({
        ...company,
        selected: true,
      });
    } else {
      localStorage.removeItem('companyId');
      this.managedCompaniesService.updateManagedCompanies({
        ...this.companies.find((x) => x.selected),
        selected: false,
      });
    }

    const currentUrl = this.currentUrl || this.router.url;
    const techEditorUrl = buildTechEditorCompanySwitchUrl(currentUrl, company.id);
    const isCompanyPage = /^\/company(?:-v2)?(?:\/|$)/.test(currentUrl.split(/[?#]/)[0]);
    const isInsightsPage = currentUrl.includes('/buyer-insights');

    if (techEditorUrl) {
      location.href = techEditorUrl;
    } else if (isCompanyPage) {
      location.href = '/company-v2/' + company.id;
    } else if (isInsightsPage) {
      location.href = '/buyer-insights';
    } else {
      location.reload();
    }
    // const guidRegex = /[0-9A-Fa-f]{32}$/; // регулярное выражение для GUID

    // if (guidRegex.test(location.pathname)) {
    //   const pathWithoutGuid = location.pathname.replace(guidRegex, '');
    //   location.href = pathWithoutGuid;
    // } else {
    //   location.href = location.pathname;
    // }


  }

  getSelectedCompany() {
    const selectedCompany = localStorage.getItem('companyId');
    if (selectedCompany) {
      this.selectedCompany = this.companies.find(
        (x) => x.id == selectedCompany
      );
      this.managedCompaniesService.updateManagedCompanies({
        ...this.selectedCompany,
        selected: true,
      });
    } else {
      this.selectedCompany = this.companies.find(
        (x) => x.id == this.userProfileDataService.userCompanyData?.id
      );
    }
  }

  public deleteCompanyFromManagmentList(companyId, event?: MouseEvent) {
    event?.stopPropagation();
    event?.preventDefault();

    if (
      !companyId ||
      companyId === this.profileCompanyId ||
      companyId === this.selectedCompany?.id
    ) {
      return;
    }

    this.managedCompaniesService.deleteManagedCompany(companyId);
    // this.getSelectedCompany();
  }

  onLogoutAs(){
   this.logoutAs.emit()
  }

  onLogout(){
   this.logout.emit()
  }

  goToMyProfiles(){
    // this.contactSearchFiltersService.resetFilters();
    // this.contactSearchFiltersService.owned = true;
    // this.contactSearchFiltersService.filtersChanged=true;
    // localStorage.setItem('searchActiveTab', 'contacts');
    //this.router.navigate(['/products'],{ queryParams: { timestamp: new Date().getTime() }});
    this.router.navigate(['/my-contacts']);
  }

  goTo(str: string) {
    this.router.navigate([str]);
  }


  onThemeToggle(event: any) {
    if (this.isRhetorikThemeActive) {
      this.themeService.activateTheme('rhetorik-theme');
    } else {
      this.themeService.deactivateTheme('rhetorik-theme');
    }
  }

  startAi() {

    const dialogHeight = 'calc(100vh - 70px)';

    const dialogRef = this.dialog.open(AiChatComponent, {
      position: { right: '0', bottom:'0'},
      disableClose: true,
      hasBackdrop:false,
      height: dialogHeight,
      width: '860px',
      maxWidth: 'calc(100vw - 24px)',
      panelClass: ['animate__animated', 'animate__slideInUp', 'rachel-chat-dialog-panel'],
      enterAnimationDuration: '200ms',
      exitAnimationDuration: '400ms',
    });
    this.showAiChat = false;

    dialogRef.beforeClosed().subscribe(() => {
      this.showAiChat = true;
      dialogRef.removePanelClass('animate__slideInUp');
      dialogRef.addPanelClass('animate__slideOutDown');
    });

  }
}
